import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Loader2,
  PlayCircle,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { adminApi, type AdminContent } from '@/api/admin'
import type { GameContentPathNode } from '@/api/games'

const PATH_LEVELS = ['curriculum', 'course', 'module', 'lesson'] as const

export interface GameLessonPathSelection {
  curriculum: GameContentPathNode
  course: GameContentPathNode
  module: GameContentPathNode
  lesson: GameContentPathNode
}

interface GameLessonPathPickerProps {
  existingPathKeys: Set<string>
  onPick: (selection: GameLessonPathSelection) => void
}

const LEVEL_LABELS: Record<(typeof PATH_LEVELS)[number], string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨',
}

const LEVEL_ICONS: Record<(typeof PATH_LEVELS)[number], React.ElementType> = {
  curriculum: BookOpen,
  course: FolderTree,
  module: FolderTree,
  lesson: PlayCircle,
}

export function gameLessonPathKey(path: GameLessonPathSelection): string {
  return [
    path.curriculum.contentId,
    path.course.contentId,
    path.module.contentId,
    path.lesson.contentId,
  ].join(':')
}

export function buildGameLessonPathSelection(
  ancestors: AdminContent[]
): GameLessonPathSelection | null {
  if (
    ancestors.length !== PATH_LEVELS.length ||
    ancestors.some((content, index) => content.hierarchyLevel !== PATH_LEVELS[index])
  ) {
    return null
  }

  const [curriculum, course, module, lesson] = ancestors
  return {
    curriculum: { contentId: curriculum.id, title: curriculum.title },
    course: { contentId: course.id, title: course.title },
    module: { contentId: module.id, title: module.title },
    lesson: { contentId: lesson.id, title: lesson.title },
  }
}

export function GameLessonPathPicker({
  existingPathKeys,
  onPick,
}: GameLessonPathPickerProps) {
  const [expandedPathKeys, setExpandedPathKeys] = useState<Set<string>>(new Set())
  const {
    data: curricula,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'game-lesson-path-picker', 'curricula'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
  })

  const toggleExpanded = (pathKey: string) => {
    setExpandedPathKeys((previous) => {
      const next = new Set(previous)
      if (next.has(pathKey)) {
        next.delete(pathKey)
      } else {
        next.add(pathKey)
      }
      return next
    })
  }

  if (isLoading) {
    return <GameLessonPathPickerFeedback state="loading" />
  }

  if (error) {
    return <GameLessonPathPickerFeedback state="error" onRetry={() => refetch()} />
  }

  if (!curricula?.length) {
    return <GameLessonPathPickerFeedback state="empty" />
  }

  return (
    <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1">
      {curricula.map((curriculum) => (
        <GameLessonPathNode
          key={curriculum.id}
          content={curriculum}
          ancestors={[]}
          expandedPathKeys={expandedPathKeys}
          existingPathKeys={existingPathKeys}
          onPick={onPick}
          onToggle={toggleExpanded}
        />
      ))}
    </div>
  )
}

interface GameLessonPathPickerFeedbackProps {
  state: 'loading' | 'error' | 'empty'
  onRetry?: () => void
}

export function GameLessonPathPickerFeedback({
  state,
  onRetry,
}: GameLessonPathPickerFeedbackProps) {
  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
        <Loader2 className="size-4 animate-spin" />
        커리큘럼을 불러오는 중입니다.
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p>커리큘럼 목록을 불러오지 못했습니다.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1.5 font-medium text-red-700 hover:text-red-900"
        >
          <RefreshCw className="size-3.5" />
          다시 시도
        </button>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
      선택할 수 있는 커리큘럼이 없습니다.
    </div>
  )
}

interface GameLessonPathNodeProps {
  content: AdminContent
  ancestors: AdminContent[]
  expandedPathKeys: Set<string>
  existingPathKeys: Set<string>
  onPick: (selection: GameLessonPathSelection) => void
  onToggle: (pathKey: string) => void
}

function GameLessonPathNode({
  content,
  ancestors,
  expandedPathKeys,
  existingPathKeys,
  onPick,
  onToggle,
}: GameLessonPathNodeProps) {
  const path = [...ancestors, content]
  const pathKey = path.map((item) => item.id).join(':')
  const levelIndex = PATH_LEVELS.indexOf(content.hierarchyLevel as (typeof PATH_LEVELS)[number])
  const isLesson = content.hierarchyLevel === 'lesson'
  const isExpanded = expandedPathKeys.has(pathKey)
  const expectedChildLevel = PATH_LEVELS[levelIndex + 1]
  const selection = isLesson ? buildGameLessonPathSelection(path) : null
  const isAlreadySelected = selection
    ? existingPathKeys.has(gameLessonPathKey(selection))
    : false

  const {
    data: children,
    error,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'game-lesson-path-picker', 'children', content.id],
    queryFn: () => adminApi.getChildContents(content.id),
    enabled: isExpanded && !isLesson,
  })

  if (levelIndex < 0) {
    return null
  }

  const Icon = LEVEL_ICONS[PATH_LEVELS[levelIndex]]
  const visibleChildren = children?.filter(
    (child) => child.hierarchyLevel === expectedChildLevel
  )

  return (
    <div>
      <div
        className="group flex min-h-9 items-center gap-2 rounded-md px-2 text-sm hover:bg-slate-50"
        style={{ paddingLeft: `${ancestors.length * 18 + 8}px` }}
      >
        {isLesson ? (
          <span className="size-4 shrink-0" />
        ) : (
          <button
            type="button"
            onClick={() => onToggle(pathKey)}
            className="flex size-5 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            aria-label={`${content.title} ${isExpanded ? '접기' : '펼치기'}`}
          >
            {isFetching ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : isExpanded ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
          </button>
        )}
        <Icon className="size-3.5 shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1 truncate text-slate-700">{content.title}</span>
        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-400">
          {LEVEL_LABELS[PATH_LEVELS[levelIndex]]}
        </span>
        {selection && (
          isAlreadySelected ? (
            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
              선택됨
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onPick(selection)}
              className="flex size-6 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100"
              title="이 경로 추가"
            >
              <Plus className="size-3.5" />
            </button>
          )
        )}
      </div>

      {isExpanded && error && (
        <div
          className="flex items-center gap-2 py-2 text-xs text-red-600"
          style={{ paddingLeft: `${(ancestors.length + 1) * 18 + 8}px` }}
        >
          하위 콘텐츠를 불러오지 못했습니다.
          <button type="button" onClick={() => refetch()} className="font-medium underline">
            재시도
          </button>
        </div>
      )}

      {isExpanded && !error && !isFetching && visibleChildren?.length === 0 && (
        <div
          className="py-2 text-xs text-slate-400"
          style={{ paddingLeft: `${(ancestors.length + 1) * 18 + 8}px` }}
        >
          연결된 {expectedChildLevel ? LEVEL_LABELS[expectedChildLevel] : '하위 콘텐츠'}가 없습니다.
        </div>
      )}

      {isExpanded && visibleChildren?.map((child) => (
        <GameLessonPathNode
          key={`${pathKey}:${child.id}`}
          content={child}
          ancestors={path}
          expandedPathKeys={expandedPathKeys}
          existingPathKeys={existingPathKeys}
          onPick={onPick}
          onToggle={onToggle}
        />
      ))}
    </div>
  )
}
